import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaRobot, FaMicrophone, FaMicrophoneSlash, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';
import { MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { getSpeechLocaleTag } from '../utils/format';

const AIChatWidget = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // P2-21: on phones the floating button sat on top of the right-aligned
  // values of list cards; it slides away while scrolling down and comes
  // back on scrolling up (or at the top of the page). Idle at the top it is
  // also smaller and tucked into the corner on phones, so it covers less of
  // the first cards (the page keeps bottom padding to scroll content clear).
  const [fabHidden, setFabHidden] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY || 0;
    const onScroll = () => {
      const y = window.scrollY || 0;
      if (y < 80) setFabHidden(false);
      else if (y > lastY + 8) setFabHidden(true);
      else if (y < lastY - 8) setFabHidden(false);
      if (Math.abs(y - lastY) > 8) lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Voice input states
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  // TTS states
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsSupported, setTtsSupported] = useState(false);

  // Initialize Speech Recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = getSpeechLocaleTag(); // active UI language

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputMessage(transcript);

        // If it's final result, we can optionally auto-send
        if (event.results[0].isFinal) {
          setIsListening(false);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Check TTS support
    if ('speechSynthesis' in window) {
      setTtsSupported(true);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Toggle voice input
  const toggleListening = useCallback(() => {
    if (!speechSupported || !recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputMessage('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening, speechSupported]);

  // Text-to-Speech function
  const speakText = useCallback((text) => {
    if (!ttsSupported || !ttsEnabled) return;

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = getSpeechLocaleTag();
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  }, [ttsEnabled, ttsSupported]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history when widget opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadChatHistory();
    }
  }, [isOpen]);

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/ai-assistant/chat-history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          setSessionId(data.messages[0].session_id);
        }
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/ai-assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: inputMessage,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Save session_id for next messages
        if (!sessionId) {
          setSessionId(data.session_id);
        }

        const aiMessage = {
          role: 'assistant',
          content: data.response,
          created_at: new Date().toISOString()
        };

        setMessages(prev => [...prev, aiMessage]);

        // Speak the AI response if TTS is enabled
        if (ttsEnabled) {
          speakText(data.response);
        }
      } else {
        const errorData = await response.json();
        const errorMessage = {
          role: 'assistant',
          content: t('shell.aiChat.errorWithDetail', { detail: errorData.detail || t('shell.aiChat.unknownError') }),
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: t('shell.aiChat.connectionError'),
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${backendUrl}/api/ai-assistant/chat-history`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setMessages([]);
      setSessionId(null);
    } catch (error) {
      console.error('Error clearing chat history:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          aria-label={t('shell.aiChat.open')}
          data-testid="ai-chat-fab"
          className={`ai-chat-fab fixed bottom-6 right-6 max-sm:bottom-3 max-sm:right-3 max-sm:p-2.5 max-sm:opacity-90 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:scale-110 z-50 ${fabHidden ? 'max-sm:translate-y-24 max-sm:opacity-0 max-sm:pointer-events-none' : ''}`}
        >
          <FaRobot className="w-7 h-7 max-sm:w-5 max-sm:h-5" />
        </button>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white dark:bg-surface-900 rounded-lg shadow-2xl flex flex-col z-50">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FaRobot size={24} />
              <div>
                <h3 className="font-semibold">{t('shell.aiChat.title')}</h3>
                <p className="text-xs opacity-90">{t('shell.aiChat.howCanIHelp')}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* TTS Toggle Button */}
              {ttsSupported && (
                <button
                  onClick={() => {
                    if (isSpeaking) {
                      stopSpeaking();
                    }
                    setTtsEnabled(!ttsEnabled);
                  }}
                  className={`p-1.5 rounded transition-colors ${
                    ttsEnabled
                      ? 'bg-green-500 hover:bg-green-600'
                      : 'hover:bg-blue-800 opacity-70'
                  }`}
                  title={ttsEnabled ? t('shell.aiChat.disableVoiceReplies') : t('shell.aiChat.enableVoiceReplies')}
                >
                  {isSpeaking ? (
                    <FaVolumeUp size={16} className="animate-pulse" />
                  ) : ttsEnabled ? (
                    <FaVolumeUp size={16} />
                  ) : (
                    <FaVolumeMute size={16} />
                  )}
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                aria-label={t('shell.aiChat.close')}
                className="hover:bg-blue-800 p-1 rounded transition-colors"
              >
                <MdClose size={20} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-surface-900">
            {messages.length === 0 && (
              <div className="text-center text-gray-500 dark:text-surface-400 mt-8">
                <FaRobot size={48} className="mx-auto mb-4 text-blue-600" />
                <p className="text-sm">{t('shell.aiChat.greeting')}</p>
                <p className="text-xs mt-2">{t('shell.aiChat.howCanIHelp')}</p>
                <div className="mt-4 text-xs text-left space-y-2">
                  <p className="font-semibold">{t('shell.aiChat.exampleQuestions')}</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-surface-400">
                    <li>{t('shell.aiChat.example1')}</li>
                    <li>{t('shell.aiChat.example2')}</li>
                    <li>{t('shell.aiChat.example3')}</li>
                  </ul>
                </div>
                {speechSupported && (
                  <div className="mt-4 text-xs text-gray-500 dark:text-surface-400 flex items-center justify-center space-x-1">
                    <FaMicrophone size={12} />
                    <span>{t('shell.aiChat.voiceHint')}</span>
                  </div>
                )}
              </div>
            )}
            
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none'
                      : 'bg-white dark:bg-surface-900 text-gray-800 dark:text-surface-100 rounded-bl-none shadow-md'
                  }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center space-x-2 mb-2">
                      <FaRobot size={16} className="text-blue-600" />
                      <span className="text-xs font-semibold text-blue-600">{t('shell.aiChat.title')}</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-surface-900 p-3 rounded-lg shadow-md">
                  <div className="flex items-center space-x-2">
                    <FaRobot size={16} className="text-blue-600" />
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white dark:bg-surface-900 border-t rounded-b-lg">
            {messages.length > 0 && (
              <button
                onClick={clearHistory}
                className="text-xs text-red-600 hover:text-red-700 mb-2"
              >
                {t('shell.aiChat.clearHistory')}
              </button>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isListening ? t('shell.aiChat.listening') : t('shell.aiChat.inputPlaceholder')}
                className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isListening ? 'border-red-400 bg-red-50' : 'border-gray-300 dark:border-surface-700'
                }`}
                disabled={isLoading || isListening}
              />
              {/* Microphone Button */}
              {speechSupported && (
                <button
                  onClick={toggleListening}
                  disabled={isLoading}
                  className={`p-2 rounded-lg transition-all ${
                    isListening
                      ? 'bg-red-500 text-white animate-pulse hover:bg-red-600'
                      : 'bg-gray-200 dark:bg-surface-700 text-gray-600 dark:text-surface-400 hover:bg-gray-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={isListening ? t('shell.aiChat.stopListening') : t('shell.aiChat.sendVoiceMessage')}
                >
                  {isListening ? (
                    <FaMicrophoneSlash size={20} />
                  ) : (
                    <FaMicrophone size={20} />
                  )}
                </button>
              )}
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                aria-label={t('shell.aiChat.send')}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                <IoSend size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
